import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useAlert } from '../context/AlertContext';
import { api } from '../services/api';

export default function AuthModal({ onClose }) {
  const [mode, setMode] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [fullName, setFullName] = useState('');
  const [role, setRole] = useState('student');
  const [studentTier, setStudentTier] = useState('school');
  const [subjects, setSubjects] = useState('');
  const [qualification, setQualification] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [authMethod, setAuthMethod] = useState('password');
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [otpDevCode, setOtpDevCode] = useState('');
  const [sendingOtp, setSendingOtp] = useState(false);
  const { signInWithEmail, signUpWithEmail, signUpWithOtp, signInWithOtp, signInWithGoogle } = useAuth();
  const { showAlert } = useAlert();
  const navigate = useNavigate();

  const navigateToDashboard = (userRole) => {
    if (userRole === 'mentor') navigate('/mentor-dashboard');
    else if (userRole === 'admin') navigate('/admin');
    else if (userRole === 'parent') navigate('/parent-card');
    else navigate('/dashboard');
  };

  const handleGoogleAuth = async () => {
    setError('');
    setLoading(true);
    try {
      const data = await signInWithGoogle();
      onClose();
      navigateToDashboard(data?.user?.role || 'student');
    } catch (err) {
      setError(err.message || 'Google sign-in failed');
    } finally {
      setLoading(false);
    }
  };

  const handleSendOtp = async () => {
    setError('');
    if (!email) { setError('Enter your email first'); return; }
    setSendingOtp(true);
    try {
      const purpose = mode === 'login' ? 'login' : 'register';
      const result = await api.requestOtp(email, purpose);
      setOtpSent(true);
      setOtpDevCode(result?.devCode || '');
      showAlert('Code Sent', `We've sent a 6-digit code to ${email}. It expires in 10 minutes.`);
    } catch (err) {
      setError(err.message || 'Could not send code');
    } finally {
      setSendingOtp(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (authMethod === 'password' && mode === 'register' && password !== confirm) {
      setError('Passwords do not match');
      return;
    }
    if (mode === 'register' && role === 'mentor' && !subjects.trim()) {
      setError('Please list at least one subject you can mentor in');
      return;
    }
    if (authMethod === 'otp' && !otpSent) {
      setError('Please request a code first');
      return;
    }

    setLoading(true);
    try {
      let data;
      if (authMethod === 'otp') {
        if (mode === 'login') {
          data = await signInWithOtp(email, otpCode);
        } else {
          const extra = { full_name: fullName, role };
          if (role === 'student') extra.studentTier = studentTier;
          else {
            extra.subjects = subjects.split(',').map((s) => s.trim()).filter(Boolean);
            extra.qualification = qualification;
          }
          data = await signUpWithOtp(email, password || Math.random().toString(36).slice(2, 10), otpCode, extra);
          showAlert('Account Created', 'Your email is verified and your account is ready!');
        }
      } else if (mode === 'login') {
        data = await signInWithEmail(email, password);
      } else {
        const extra = { full_name: fullName, role };
        if (role === 'student') {
          extra.studentTier = studentTier;
        } else {
          extra.subjects = subjects.split(',').map((s) => s.trim()).filter(Boolean);
          extra.qualification = qualification;
        }
        data = await signUpWithEmail(email, password, extra);
        showAlert(
          'Account Created',
          role === 'mentor'
            ? 'Welcome to Ekalavya! Your mentor profile is now pending admin review.'
            : 'Welcome to Ekalavya! Check your email to verify.'
        );
      }
      onClose();
      navigateToDashboard(data?.user?.role || role || 'student');
    } catch (err) {
      setError(err.message || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-fixed">
      <div className="modal-overlay" onClick={onClose} />
      <div className="auth-modal-container">

        {/* Left Pane */}
        <div className="auth-left-pane">
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '3rem' }}>
            <img
              src={process.env.PUBLIC_URL + '/Logo.jpeg'}
              style={{ width: 40, height: 40, borderRadius: 8, objectFit: 'cover' }}
              alt="Ekalavya Logo"
            />
            <span style={{ fontSize: '1.5rem', fontWeight: 800, color: '#0f172a' }}>Ekalavya</span>
          </div>
          <h2 style={{ fontSize: '2.5rem', fontWeight: 800, lineHeight: 1.1, marginBottom: '2rem', color: '#0f172a' }}>
            {mode === 'login' ? 'Welcome back to Ekalavya' : 'Create Your Free Account'}
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem', marginTop: 'auto' }}>
            {['Access all free courses and labs', 'Join our vibrant community', 'Connect with expert mentors', 'Track your scholarship applications', 'No credit card required'].map(feat => (
              <div key={feat} style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <div style={{ width: 24, height: 24, borderRadius: '50%', background: '#d1fae5', color: '#059669', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✓</div>
                <span style={{ fontSize: '1.05rem', color: '#475569', fontWeight: 500 }}>{feat}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Right Pane */}
        <div className="auth-right-pane">
          <button onClick={onClose} style={{ position: 'absolute', top: '1.5rem', right: '1.5rem', background: 'none', border: 'none', color: '#64748b', cursor: 'pointer' }}>
            <X size={20} />
          </button>

          <div style={{ width: '100%', maxWidth: '400px' }}>
            <div style={{ textAlign: 'right', fontSize: '0.9rem', color: '#64748b', marginBottom: '2rem' }}>
              {mode === 'login' ? (
                <>Don't have an account? <button onClick={() => setMode('register')} style={{ color: 'var(--primary-gold)', fontWeight: 700, background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit' }}>Sign up for free</button></>
              ) : (
                <>Already have an account? <button onClick={() => setMode('login')} style={{ color: 'var(--primary-gold)', fontWeight: 700, background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit' }}>Sign in</button></>
              )}
            </div>

            <h2 style={{ fontSize: '1.75rem', fontWeight: 800, color: '#0f172a', marginBottom: '1.5rem' }}>
              {mode === 'login' ? 'Scholar Login' : 'Scholar Signup'}
            </h2>

            <button
              onClick={handleGoogleAuth}
              style={{ width: '100%', padding: '1rem', background: '#f1f5f9', border: '1.5px solid #e2e8f0', color: '#0f172a', borderRadius: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.75rem', cursor: 'pointer', transition: 'all 0.2s', fontFamily: 'inherit', fontSize: '0.9rem', fontWeight: 700 }}
              onMouseOver={e => e.currentTarget.style.background = '#e2e8f0'}
              onMouseOut={e => e.currentTarget.style.background = '#f1f5f9'}
            >
              <img src="https://www.google.com/favicon.ico" style={{ width: '1.25rem', height: '1.25rem' }} alt="Google" />
              Continue with Google
            </button>

            <div className="modal-gateway-divider" style={{ margin: '1.5rem 0', position: 'relative' }}>
              <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center' }}>
                <div style={{ width: '100%', borderTop: '1px solid rgba(147, 51, 234, 0.1)' }} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <span style={{ position: 'relative', background: '#ffffff', padding: '0 0.75rem', fontSize: '0.75rem', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.1em' }}>or</span>
              </div>
            </div>

            {error && (
              <div style={{ color: '#ef4444', fontSize: '0.8rem', fontWeight: 700, textAlign: 'center', background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.2)', borderRadius: '0.75rem', padding: '0.75rem', marginBottom: '1rem' }}>
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>

              {mode === 'register' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  <label style={{ fontSize: '0.85rem', fontWeight: 700, color: '#334155' }}>I am a...</label>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    {[{ v: 'student', label: 'Student' }, { v: 'mentor', label: 'Mentor' }].map((opt) => (
                      <button key={opt.v} type="button" onClick={() => setRole(opt.v)}
                        style={{
                          flex: 1, padding: '0.75rem', borderRadius: '0.75rem', fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer',
                          border: role === opt.v ? '1.5px solid var(--primary-gold)' : '1px solid #e2e8f0',
                          background: role === opt.v ? 'rgba(147,51,234,.08)' : '#f8fafc',
                          color: role === opt.v ? 'var(--primary-gold)' : '#64748b',
                        }}>
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {mode === 'register' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  <label style={{ fontSize: '0.85rem', fontWeight: 700, color: '#334155' }}>Full Name</label>
                  <input type="text" placeholder="Your full name" value={fullName} onChange={e => setFullName(e.target.value)} required
                    style={{ width: '100%', padding: '0.875rem 1rem', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '0.75rem', outline: 'none', fontFamily: 'inherit', fontSize: '0.95rem', color: '#0f172a' }} />
                </div>
              )}

              {mode === 'register' && role === 'student' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  <label style={{ fontSize: '0.85rem', fontWeight: 700, color: '#334155' }}>Your Class</label>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    {[{ v: 'school', label: 'School' }, { v: 'intermediate', label: 'Intermediate' }, { v: 'btech', label: 'B.Tech' }].map((opt) => (
                      <button key={opt.v} type="button" onClick={() => setStudentTier(opt.v)}
                        style={{
                          flex: 1, padding: '0.7rem 0.4rem', borderRadius: '0.75rem', fontWeight: 700, fontSize: '0.8rem', cursor: 'pointer',
                          border: studentTier === opt.v ? '1.5px solid var(--primary-gold)' : '1px solid #e2e8f0',
                          background: studentTier === opt.v ? 'rgba(147,51,234,.08)' : '#f8fafc',
                          color: studentTier === opt.v ? 'var(--primary-gold)' : '#64748b',
                        }}>
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {mode === 'register' && role === 'mentor' && (
                <>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    <label style={{ fontSize: '0.85rem', fontWeight: 700, color: '#334155' }}>Subjects you can mentor in</label>
                    <input type="text" placeholder="e.g. DSA, Physics, Calculus" value={subjects} onChange={e => setSubjects(e.target.value)} required
                      style={{ width: '100%', padding: '0.875rem 1rem', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '0.75rem', outline: 'none', fontFamily: 'inherit', fontSize: '0.95rem', color: '#0f172a' }} />
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    <label style={{ fontSize: '0.85rem', fontWeight: 700, color: '#334155' }}>Qualification</label>
                    <input type="text" placeholder="e.g. M.Tech CSE, IIT Hyderabad" value={qualification} onChange={e => setQualification(e.target.value)}
                      style={{ width: '100%', padding: '0.875rem 1rem', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '0.75rem', outline: 'none', fontFamily: 'inherit', fontSize: '0.95rem', color: '#0f172a' }} />
                  </div>
                  <div style={{ fontSize: '0.78rem', color: '#d97706', background: 'rgba(217,119,6,.08)', border: '1px solid rgba(217,119,6,.2)', borderRadius: '0.75rem', padding: '0.6rem 0.85rem' }}>
                    Mentor accounts require admin approval before you can host sessions.
                  </div>
                </>
              )}

              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <label style={{ fontSize: '0.85rem', fontWeight: 700, color: '#334155' }}>Email Address</label>
                <input type="email" placeholder="scholar@ekalavya.edu" value={email} onChange={e => { setEmail(e.target.value); setOtpSent(false); }} required
                  style={{ width: '100%', padding: '0.875rem 1rem', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '0.75rem', outline: 'none', fontFamily: 'inherit', fontSize: '0.95rem', color: '#0f172a' }} />
              </div>

              <div style={{ display: 'flex', gap: '1.25rem', fontSize: '0.78rem' }}>
                <button type="button" onClick={() => { setAuthMethod('password'); setError(''); }}
                  style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontWeight: 700, color: authMethod === 'password' ? 'var(--primary-gold)' : '#94a3b8', borderBottom: authMethod === 'password' ? '2px solid var(--primary-gold)' : '2px solid transparent', paddingBottom: '0.25rem', transition: 'all 0.15s' }}>
                  Password
                </button>
                <button type="button" onClick={() => { setAuthMethod('otp'); setError(''); }}
                  style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontWeight: 700, color: authMethod === 'otp' ? 'var(--primary-gold)' : '#94a3b8', borderBottom: authMethod === 'otp' ? '2px solid var(--primary-gold)' : '2px solid transparent', paddingBottom: '0.25rem', transition: 'all 0.15s' }}>
                  Email Code (OTP)
                </button>
              </div>

              {authMethod === 'password' ? (
                <>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <label style={{ fontSize: '0.85rem', fontWeight: 700, color: '#334155' }}>Password</label>
                      {mode === 'login' && <span style={{ fontSize: '0.8rem', color: 'var(--primary-gold)', cursor: 'pointer', fontWeight: 600 }}>Forgot?</span>}
                    </div>
                    <div style={{ position: 'relative' }}>
                      <input type={showPassword ? 'text' : 'password'} placeholder="Enter your password" value={password} onChange={e => setPassword(e.target.value)} required
                        style={{ width: '100%', padding: '0.875rem 2.5rem 0.875rem 1rem', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '0.75rem', outline: 'none', fontFamily: 'inherit', fontSize: '0.95rem', color: '#0f172a' }} />
                      <button type="button" onClick={() => setShowPassword(!showPassword)} style={{ position: 'absolute', right: '0.75rem', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', display: 'flex', padding: 0 }}>
                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                  </div>
                  {mode === 'register' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                      <label style={{ fontSize: '0.85rem', fontWeight: 700, color: '#334155' }}>Confirm Password</label>
                      <input type={showPassword ? 'text' : 'password'} placeholder="Confirm your password" value={confirm} onChange={e => setConfirm(e.target.value)} required
                        style={{ width: '100%', padding: '0.875rem 1rem', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '0.75rem', outline: 'none', fontFamily: 'inherit', fontSize: '0.95rem', color: '#0f172a' }} />
                    </div>
                  )}
                </>
              ) : (
                <div className="fade-in-up" style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  {!otpSent ? (
                    <button type="button" onClick={handleSendOtp} disabled={sendingOtp || !email}
                      style={{ width: '100%', padding: '0.85rem', background: '#f8fafc', border: '1px solid #e2e8f0', color: 'var(--navy-dark)', borderRadius: '0.75rem', fontWeight: 700, fontSize: '0.9rem', cursor: sendingOtp || !email ? 'not-allowed' : 'pointer', fontFamily: 'inherit', opacity: sendingOtp || !email ? 0.6 : 1 }}>
                      {sendingOtp ? 'Sending code...' : 'Send verification code'}
                    </button>
                  ) : (
                    <>
                      <div>
                        <label style={{ fontSize: '0.85rem', fontWeight: 700, color: '#334155' }}>6-Digit Code</label>
                        <input type="text" inputMode="numeric" maxLength={6} placeholder="123456" value={otpCode} onChange={e => setOtpCode(e.target.value.replace(/\D/g, ''))} required
                          style={{ width: '100%', padding: '0.875rem 1rem', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '0.75rem', outline: 'none', fontFamily: 'inherit', fontSize: '1.1rem', letterSpacing: '0.3em', textAlign: 'center', color: '#0f172a', marginTop: '0.5rem' }} />
                      </div>
                      {otpDevCode && (
                        <div style={{ fontSize: '0.72rem', color: '#d97706', background: 'rgba(217,119,6,.08)', border: '1px solid rgba(217,119,6,.2)', borderRadius: '0.6rem', padding: '0.5rem 0.75rem' }}>
                          Dev mode: your code is <b>{otpDevCode}</b>
                        </div>
                      )}
                      {mode === 'register' && (
                        <div>
                          <label style={{ fontSize: '0.85rem', fontWeight: 700, color: '#334155' }}>Password (for future logins)</label>
                          <input type={showPassword ? 'text' : 'password'} placeholder="Choose a password" value={password} onChange={e => setPassword(e.target.value)} required
                            style={{ width: '100%', padding: '0.875rem 1rem', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '0.75rem', outline: 'none', fontFamily: 'inherit', fontSize: '0.95rem', color: '#0f172a', marginTop: '0.5rem' }} />
                        </div>
                      )}
                      <button type="button" onClick={handleSendOtp} disabled={sendingOtp}
                        style={{ background: 'none', border: 'none', color: 'var(--primary-gold)', fontWeight: 700, fontSize: '0.78rem', cursor: 'pointer', alignSelf: 'flex-start', padding: 0 }}>
                        {sendingOtp ? 'Resending...' : "Didn't get it? Resend code"}
                      </button>
                    </>
                  )}
                </div>
              )}

              <button type="submit" disabled={loading}
                style={{ background: 'var(--primary-gold)', fontWeight: 700, width: '100%', padding: '1rem', color: 'white', borderRadius: '1rem', marginTop: '0.5rem', cursor: 'pointer', opacity: loading ? 0.7 : 1, fontFamily: 'inherit', fontSize: '0.95rem', border: 'none', transition: 'all 0.3s', boxShadow: '0 4px 14px var(--gold-shadow)' }}
                onMouseOver={e => e.currentTarget.style.transform = 'translateY(-2px)'}
                onMouseOut={e => e.currentTarget.style.transform = 'translateY(0)'}
              >
                {loading ? 'Authenticating...' : 'Continue'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}